import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Stack,
  CircularProgress,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  IconButton,
  Paper,
  Card,
  CardActionArea,
  CardContent,
  Avatar,
  Tooltip,
  InputAdornment,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";

import axios from "axios";
import Images from "../../../utils/Images";
import { CategoryFilterChip } from "../../../components/FilterChip";
import { API } from "../../../utils/Api";
import { toast } from "react-toastify";

const todayYYYYMMDD = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

function OnlineItemSales() {
  const token = localStorage.getItem("token");
  const [outletData] = useState(JSON.parse(localStorage.getItem("outletData")));

  // data
  const [categories, setCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // filters
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  // selection
  const [selectedProduct, setSelectedProduct] = useState(null);

  // sale header
  const [platform, setPlatform] = useState("ZOMATO");
  const [date, setDate] = useState(todayYYYYMMDD());

  // current item config
  const [qty, setQty] = useState(1);
  const [price, setPrice] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [addonSelections, setAddonSelections] = useState({});

  // cart
  const [cartItems, setCartItems] = useState([]);

  // submit state
  const [posting, setPosting] = useState(false);

  const flattenCategoriesToProducts = (categoryList) => {
    if (!Array.isArray(categoryList)) return [];
    return categoryList.flatMap((cat) =>
      (cat.products || []).map((prod) => ({
        ...prod,
        category: { _id: cat._id, name: cat.name, description: cat.description },
      }))
    );
  };

  const getCategoriesProductsList = async () => {
    if (!outletData?._id) return;

    try {
      setLoadingProducts(true);
      const response = await axios.get(`${API.BASE_URL}product/getProductsWithAddons`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        params: { outletId: outletData._id },
      });

      const fetchedCategories = response?.data?.data?.categories || [];
      setCategories(fetchedCategories);

      const flattened = flattenCategoriesToProducts(fetchedCategories);
      setAllProducts(flattened);
    } catch (e) {
      console.error("Error fetching products with addons:", e);
      toast.error("Failed to fetch products.");
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    getCategoriesProductsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // derive visible products (no extra products state needed)
  const products = useMemo(() => {
    const search = (searchQuery || "").toLowerCase().trim();
    return allProducts.filter((p) => {
      const categoryOk = selectedCategoryId ? p?.category?._id === selectedCategoryId : true;
      if (!categoryOk) return false;

      if (!search) return true;

      const nameMatch = p.name?.toLowerCase().includes(search);
      const descMatch = p.description?.toLowerCase().includes(search);
      const keywordsMatch = p.keywords?.toLowerCase().includes(search);
      return nameMatch || descMatch || keywordsMatch;
    });
  }, [allProducts, searchQuery, selectedCategoryId]);

  useEffect(() => {
    setAddonSelections({});
    setQty(1);
    setDiscount(0);

    const p =
      selectedProduct?.newOfferPrice ??
      selectedProduct?.offerPrice ??
      selectedProduct?.defaultOfferPrice ??
      selectedProduct?.price ??
      0;

    setPrice(num(p));
  }, [selectedProduct]);

  const currentTotal = useMemo(
    () => Math.max(0, num(qty) * num(price) - num(discount)),
    [qty, price, discount]
  );

  const cartGrandTotal = useMemo(
    () => cartItems.reduce((sum, item) => sum + num(item.total), 0),
    [cartItems]
  );

  const getValidAddonValues = (addon) => {
    if (!addon?.values?.length) return [];
    return addon.values.filter((v) => {
      const isActiveOk = v?.status !== "INACTIVE" || v?.isDefault;
      const notExcluded = !v?.excludedOutlets?.includes(outletData?._id) || v?.isDefault;
      const notIconInfo = !v?.iconInfo;
      return isActiveOk && notExcluded && notIconInfo;
    });
  };

  const selectAddonValue = (addon, value) => {
    setAddonSelections((prev) => ({
      ...prev,
      [addon._id]: {
        addonId: addon._id,
        addonValueId: value._id,
        addonTitle: addon.title,
        addonValueTitle: value.subLabel || value.value || "",
      },
    }));
  };

  const validateCurrentItem = () => {
    if (!selectedProduct?._id) return "Please select a product.";
    if (selectedProduct?.isExhausted) return "Product is exhausted.";
    if (num(qty) <= 0) return "Quantity must be greater than 0.";

    if (selectedProduct?.hasAddon && Array.isArray(selectedProduct?.addons)) {
      const requiredAddons = selectedProduct.addons
        .filter((a) => !a?.excludedOutlets?.includes(outletData?._id))
        .filter((a) => a?.isOptional === false);

      for (const addon of requiredAddons) {
        const validValues = getValidAddonValues(addon);
        if (!validValues.length) continue;
        if (!addonSelections[addon._id]) return `Select required addon: ${addon.title}`;
      }
    }

    return null;
  };

  const handleAddItemToCart = () => {
    const err = validateCurrentItem();
    if (err) return toast.error(err);

    const addonsArray = Object.values(addonSelections);

    const payloadItem = {
      platform,
      date,
      product: selectedProduct._id,
      productName: selectedProduct.name,
      addons: addonsArray,
      quantity: num(qty),
      price: num(price),
      discount: num(discount),
      total: num(currentTotal),
    };

    setCartItems((prev) => [...prev, payloadItem]);
    toast.success("Item added to sale.");
  };

  const removeCartItem = (index) => {
    setCartItems((prev) => prev.filter((_, i) => i !== index));
    toast.info("Item removed.");
  };

  const submitSale = async () => {
    if (!cartItems.length) return toast.error("Add at least 1 item to submit.");

    const toastId = toast.loading("Posting sale...");
    try {
      setPosting(true);

      for (const item of cartItems) {
        const body = {
          platform: item.platform,
          date: item.date,
          product: item.product,
          outlet: outletData?._id,
          addons: item.addons,
          quantity: item.quantity,
          price: item.price,
          discount: item.discount,
          total: item.total,
        };

        await axios.post(`${API.BASE_URL}third-party-sales`, body, {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
      }

      setCartItems([]);
      toast.update(toastId, {
        render: "Third-party sale posted successfully.",
        type: "success",
        isLoading: false,
        autoClose: 2500,
      });
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Posting failed";
      toast.update(toastId, {
        render: msg,
        type: "error",
        isLoading: false,
        autoClose: 3500,
      });
    } finally {
      setPosting(false);
    }
  };

  const getSales = async () => {
    try{
        const response = axios.get(`${API.BASE_URL}third-party-sales`, {
            headers: {
                Authorization: `Bearer ${token}`
            },
        });
    } catch(error) {
        console.error(error);
    }
  };

  useEffect(() => {
    getSales();
  },[]);

  return (
    <Box sx={{ height: "100vh", backgroundColor: "#f5f6f8" }}>
      <Grid container sx={{ height: "100%", pt: "100px" }}>
        {/* LEFT */}
        <Grid item md={7} sx={{ height: "100%", p: 2.5 }}>
          <Paper
            elevation={0}
            sx={{
              height: "100%",
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid #e6e6e6",
              backgroundColor: "#fff",
            }}
          >
            {/* Header */}
            <Box sx={{ p: 2, borderBottom: "1px solid #eee", background: "#fff" }}>
              <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 1 }}>Products</Typography>

              <TextField
                fullWidth
                size="small"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ backgroundColor: "#fafafa", borderRadius: 2 }}
              />

              {/* Chips in GRID (no scroll) */}
              <Grid container spacing={1} sx={{ mt: 1.5, justifyContent: 'center' }}>
                {categories.map((category) => {
                  const isSelected = selectedCategoryId === category._id;
                  return (
                    <Grid item xs={6} sm={4} md={3} lg={2} key={category._id}>
                      <CategoryFilterChip
                        filterText={category.name}
                        isSelected={isSelected}
                        onClick={() => setSelectedCategoryId(isSelected ? null : category._id)}
                      />
                    </Grid>
                  );
                })}

                {selectedCategoryId && (
                  <Grid item xs={6} sm={4} md={3} lg={2}>
                    <Chip
                      label="Clear"
                      variant="outlined"
                      onClick={() => setSelectedCategoryId(null)}
                      sx={{ width: "100%", borderRadius: 999 }}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>

            {/* Body */}
            <Box sx={{ p: 2, height: "calc(100% - 140px)", overflow: "auto" }}>
              {loadingProducts ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <CircularProgress sx={{ color: "#111" }} />
                  <Typography sx={{ mt: 1, opacity: 0.7 }}>Fetching products...</Typography>
                </Box>
              ) : products.length ? (
                <Grid container spacing={2} justifyContent={'center'}>
                  {products.map((product) => {
                    const active = selectedProduct?._id === product._id;
                    return (
                      <Grid item xs={12} sm={6} md={3} lg={4} key={product._id}>
                        <Card
                          variant="outlined"
                          sx={{
                            borderRadius: 3,
                            overflow: "hidden",
                            borderColor: active ? "#00A4E2" : "#eee",
                            boxShadow: active ? "0 0 0 2px rgba(0,164,226,0.25)" : "none",
                            backgroundColor: product.isExhausted ? "#FFF4F4" : "#fff",
                          }}
                        >
                          <CardActionArea onClick={() => setSelectedProduct(product)}>
                            <CardContent sx={{ p: 1.5 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                                <Avatar
                                  variant="rounded"
                                  src={product.displayImageUrl || product.displayImage}
                                  alt={product.name}
                                  imgProps={{
                                    onError: (e) => {
                                      e.currentTarget.onerror = null;
                                      e.currentTarget.src = Images.product;
                                    },
                                  }}
                                  sx={{ width: 52, height: 52, borderRadius: 2 }}
                                />

                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Typography
                                    sx={{
                                      fontSize: 14,
                                      fontWeight: 800,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {product.name}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: 12,
                                      opacity: 0.7,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {product.category?.name || "-"}
                                  </Typography>
                                </Box>

                                {product.isExhausted ? (
                                  <Tooltip title="Exhausted">
                                    <ReportProblemIcon sx={{ color: "#d32f2f" }} />
                                  </Tooltip>
                                ) : null}
                              </Box>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              ) : (
                <Typography sx={{ textAlign: "center", py: 6, opacity: 0.7 }}>
                  No products available.
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* RIGHT */}
        <Grid item md={5} sx={{ height: "100%", p: 2.5 }}>
          <Paper
            elevation={0}
            sx={{
              height: "100%",
              borderRadius: 3,
              border: "1px solid #e6e6e6",
              backgroundColor: "#fff",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Sticky Header */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 2,
                px: 2,
                pt: 2,
                pb: 1.5,
                borderBottom: "1px solid #eee",
                background:
                  "linear-gradient(180deg, rgba(0,164,226,0.10) 0%, rgba(0,0,0,0) 60%)",
                backdropFilter: "blur(6px)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Avatar
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: 2,
                    bgcolor: "rgba(0,164,226,0.15)",
                    color: "#00A4E2",
                    fontWeight: 900,
                  }}
                >
                  <ShoppingCartOutlinedIcon fontSize="small" />
                </Avatar>

                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 900, lineHeight: 1.1 }}>
                    Online Sale
                  </Typography>
                  <Typography sx={{ fontSize: 12, opacity: 0.7 }} noWrap>
                    Third-party • {outletData?.name || "Outlet"}
                  </Typography>
                </Box>

                <Chip
                  label={`₹${cartGrandTotal}`}
                  sx={{
                    ml: "auto",
                    fontWeight: 900,
                    bgcolor: "rgba(0,164,226,0.12)",
                    color: "#006B96",
                    borderRadius: 999,
                  }}
                />
              </Box>

              <Box sx={{ mt: 1.4, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="platform-label">Platform</InputLabel>
                  <Select
                    labelId="platform-label"
                    value={platform}
                    label="Platform"
                    onChange={(e) => setPlatform(e.target.value)}
                  >
                    <MenuItem value="ZOMATO">ZOMATO</MenuItem>
                    <MenuItem value="SWIGGY">SWIGGY</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  fullWidth
                  label="Date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </Box>

            <Box sx={{ px: 2, py: 2, overflow: "auto", flex: 1 }}>
              {/* Selected Product Card */}
              {!selectedProduct ? (
                <Box sx={{ textAlign: "center", py: 5 }}>
                  <Typography sx={{ fontSize: 13, opacity: 0.75, mb: 1 }}>
                    Select a product to start
                  </Typography>
                  <img
                    src={Images.noProductSelected}
                    alt="product-not-selected"
                    style={{ maxWidth: 220, opacity: 0.9 }}
                  />
                </Box>
              ) : (
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    borderColor: "#e9eef3",
                    background: "linear-gradient(180deg, #ffffff 0%, #fafcff 100%)",
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ p: 1.5, display: "flex", gap: 1.2, alignItems: "center" }}>
                    <Avatar
                      variant="rounded"
                      src={selectedProduct.displayImageUrl || selectedProduct.displayImage}
                      alt={selectedProduct.name}
                      imgProps={{
                        onError: (e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = Images.product;
                        },
                      }}
                      sx={{ width: 54, height: 54, borderRadius: 2 }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 900 }} noWrap>
                        {selectedProduct.name}
                      </Typography>
                      <Typography sx={{ fontSize: 12, opacity: 0.7 }} noWrap>
                        {selectedProduct.category?.name || "-"}
                      </Typography>
                    </Box>

                    {selectedProduct.isExhausted ? (
                      <Tooltip title="Exhausted">
                        <ReportProblemIcon sx={{ color: "#d32f2f" }} />
                      </Tooltip>
                    ) : (
                      <Chip
                        size="small"
                        label="Selected"
                        sx={{
                          fontWeight: 800,
                          bgcolor: "rgba(0,164,226,0.12)",
                          color: "#006B96",
                        }}
                      />
                    )}
                  </Box>

                  {/* Builder Inputs */}
                  <Box sx={{ px: 1.5, pb: 1.5 }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.1 }}>
                      <Box
                        sx={{
                          p: 1.2,
                          borderRadius: 2,
                          border: "1px solid #eef2f6",
                          backgroundColor: "#fff",
                        }}
                      >
                        <Typography sx={{ fontSize: 11, opacity: 0.7, mb: 0.6 }}>Quantity</Typography>
                        <TextField
                          size="small"
                          fullWidth
                          type="number"
                          value={qty}
                          onChange={(e) => setQty(e.target.value)}
                          inputProps={{ min: 1 }}
                        />
                      </Box>

                      <Box
                        sx={{
                          p: 1.2,
                          borderRadius: 2,
                          border: "1px solid #eef2f6",
                          backgroundColor: "#fff",
                        }}
                      >
                        <Typography sx={{ fontSize: 11, opacity: 0.7, mb: 0.6 }}>Price</Typography>
                        <TextField
                          size="small"
                          fullWidth
                          type="number"
                          value={price}
                          onChange={(e) => setPrice(e.target.value)}
                          inputProps={{ min: 0 }}
                        />
                      </Box>

                      <Box
                        sx={{
                          p: 1.2,
                          borderRadius: 2,
                          border: "1px solid #eef2f6",
                          backgroundColor: "#fff",
                        }}
                      >
                        <Typography sx={{ fontSize: 11, opacity: 0.7, mb: 0.6 }}>Discount</Typography>
                        <TextField
                          size="small"
                          fullWidth
                          type="number"
                          value={discount}
                          onChange={(e) => setDiscount(e.target.value)}
                          inputProps={{ min: 0 }}
                        />
                      </Box>

                      <Box
                        sx={{
                          p: 1.2,
                          borderRadius: 2,
                          border: "1px solid #eef2f6",
                          background:
                            "linear-gradient(180deg, rgba(0,164,226,0.10) 0%, rgba(0,0,0,0) 120%)",
                        }}
                      >
                        <Typography sx={{ fontSize: 11, opacity: 0.7, mb: 0.6 }}>Total</Typography>
                        <Typography sx={{ fontSize: 18, fontWeight: 900, lineHeight: 1 }}>
                          ₹{currentTotal}
                        </Typography>
                      </Box>
                    </Box>

                    {selectedProduct?.hasAddon && Array.isArray(selectedProduct?.addons) ? (
                      <Box sx={{ mt: 1.5 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 900, mb: 1 }}>Addons</Typography>

                        <Stack spacing={1}>
                          {selectedProduct.addons
                            .filter((a) => !a?.excludedOutlets?.includes(outletData?._id))
                            .map((addon) => {
                              const values = getValidAddonValues(addon);
                              if (!values.length) return null;

                              const selected = addonSelections?.[addon._id]?.addonValueId;

                              return (
                                <Box
                                  key={addon._id}
                                  sx={{
                                    p: 1.2,
                                    borderRadius: 2,
                                    border: "1px solid #eef2f6",
                                    backgroundColor: "#fff",
                                  }}
                                >
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 900 }}>
                                      {addon.title}
                                    </Typography>
                                    {!addon.isOptional && (
                                      <Chip size="small" label="Required" sx={{ height: 20, fontSize: 11 }} />
                                    )}
                                  </Box>

                                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                    {values.map((v) => {
                                      const active = selected === v._id;
                                      return (
                                        <Chip
                                          key={v._id}
                                          label={`${v.subLabel || v.value}${num(v.price) ? `  +₹${v.price}` : ""}`}
                                          onClick={() => selectAddonValue(addon, v)}
                                          variant={active ? "filled" : "outlined"}
                                          sx={{
                                            borderRadius: 999,
                                            ...(active
                                              ? { backgroundColor: "#00A4E2", color: "#fff" }
                                              : { borderColor: "#00A4E2", color: "#00A4E2" }),
                                          }}
                                        />
                                      );
                                    })}
                                  </Box>
                                </Box>
                              );
                            })}
                        </Stack>
                      </Box>
                    ) : null}

                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleAddItemToCart}
                      sx={{
                        mt: 1.6,
                        borderRadius: 2,
                        backgroundColor: "#111",
                        py: 1.1,
                        fontWeight: 900,
                        textTransform: "none",
                      }}
                    >
                      Add Item
                    </Button>
                  </Box>
                </Card>
              )}

              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 900 }}>
                    Sale Items <span style={{ opacity: 0.6 }}>({cartItems.length})</span>
                  </Typography>

                  {cartItems.length ? (
                    <Chip
                      size="small"
                      label={`₹${cartGrandTotal}`}
                      sx={{
                        ml: "auto",
                        fontWeight: 900,
                        bgcolor: "#f5f7fb",
                        border: "1px solid #eef2f6",
                      }}
                    />
                  ) : null}
                </Box>

                {cartItems.length === 0 ? (
                  <Typography sx={{ fontSize: 13, opacity: 0.7 }}>No items added yet.</Typography>
                ) : (
                  <Stack spacing={1}>
                    {cartItems.map((item, idx) => (
                      <Box
                        key={`${item.product}-${idx}`}
                        sx={{
                          p: 1.2,
                          border: "1px solid #eef2f6",
                          borderRadius: 2,
                          backgroundColor: "#fff",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 900 }} noWrap>
                              {item.productName}
                            </Typography>
                            <Typography sx={{ fontSize: 12, opacity: 0.7 }}>
                              Qty {item.quantity} • ₹{item.price} • Disc ₹{item.discount}
                            </Typography>
                          </Box>

                          <Typography sx={{ fontSize: 13, fontWeight: 900 }}>₹{item.total}</Typography>

                          <IconButton size="small" onClick={() => removeCartItem(idx)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>

                        {item.addons?.length ? (
                          <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                            {item.addons.map((a) => (
                              <Chip
                                key={`${a.addonId}-${a.addonValueId}`}
                                size="small"
                                label={`${a.addonTitle}: ${a.addonValueTitle}`}
                                sx={{ bgcolor: "#f7fbff", borderColor: "#d9eef9" }}
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        ) : null}
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            </Box>

            <Box
              sx={{
                position: "sticky",
                bottom: 0,
                zIndex: 2,
                p: 2,
                borderTop: "1px solid #eee",
                backgroundColor: "#fff",
              }}
            >
              <Box
                sx={{
                  mb: 1.2,
                  p: 1.2,
                  borderRadius: 2,
                  backgroundColor: "#fafafa",
                  border: "1px solid #eee",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography sx={{ fontWeight: 900, fontSize: 13 }}>Grand Total</Typography>
                <Typography sx={{ fontWeight: 900, fontSize: 13 }}>₹{cartGrandTotal}</Typography>
              </Box>

              <Button
                fullWidth
                variant="contained"
                disabled={posting || cartItems.length === 0}
                onClick={submitSale}
                sx={{
                  borderRadius: 2,
                  backgroundColor: "#00A4E2",
                  py: 1.1,
                  fontWeight: 900,
                  textTransform: "none",
                }}
              >
                {posting ? "Posting..." : "Submit Sale"}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default OnlineItemSales;
